import API from '../../../APIs/API';
import Utils from '../../../Core/Utils';
import GuaranteedIXNCallAdapter from '../../../Core/helpers/adapters/GuaranteedIXNCallAdapter';
import {AgeComputed} from '../../../Core/helpers/computed/Age';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {quoteGuards} from '../../../Core/mobx/ParameterStore/QuoteGuards';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {NargsHashMapType, ProductGuaranteedValues, ProductGuaranteedFileds as PGFields} from '../../types';
import {filterQuoteResponse} from './helpers';

const GuaranteedIXNCall = async (nargs: NargsHashMapType) => {
    const apiArgs = GuaranteedIXNCallAdapter(nargs);

    const resp = await API.Call<{errors?: object[]} & Record<string, string>[]>('ixn/v1/quotes', 'POST', {
        method: 'GET',
        data: apiArgs,
    });

    if (resp?.errors != null && resp.errors.length > 0) {
        return {error: resp.errors.map((x: any) => x.title).join('. ')};
    }

    if (!resp?.length) {
        return {error: 'could not find products with the provided information.', requestDetails: apiArgs};
    }

    // pick product by healthRatingMOO
    const healthRating = (nargs[PGFields.healthRatingMOO]?.value as string)?.toLowerCase();
    const displayProduct = resp?.find((product: Record<string, string>) => {
        const productName = product.product_name?.toLowerCase();
        return productName.includes(healthRating);
    });
    if (!displayProduct) {
        return {error: 'could not find products with the provided information.'};
    }

    const outParams: Array<{
        id: string;
        label: string;
        type: 'year' | 'currency' | 'string';
    }> = [
        {id: 'carrier_name', label: 'Carrier', type: 'string'},
        {id: 'product_name', label: 'Product name', type: 'string'},
        {id: 'product_type', label: 'Product type', type: 'string'},
        {id: 'carrier_health_category', label: 'Carrier Health Category', type: 'string'},
        {id: 'ixn_health_category', label: 'IXN Health Category', type: 'string'},
        {id: 'face_amount', label: 'Face amount', type: 'currency'},
        {id: 'premium_monthly', label: 'Monthly premium', type: 'currency'},
        {id: 'premium_quarterly', label: 'Quarterly premium', type: 'currency'},
        {id: 'premium_semi_annual', label: 'Semi-annual premium', type: 'currency'},
        {id: 'premium_annual', label: 'Annual premium', type: 'currency'},
    ];

    const out = [] as Array<{name: string; value: any; type: 'year' | 'currency' | 'string'}>;
    for (let index = 0; index < outParams.length; index += 1) {
        const op = outParams[index];
        const value = displayProduct[op.id];
        if (value != null && value != '') {
            out.push({name: op.label, value, type: op.type});
        }
    }
    let {err, faceAmount, monthlyPremium, productName} = filterQuoteResponse(out);

    return {faceAmount, monthlyPremium, productName, out, error: err, requestDetails: apiArgs};
};

export const guaranteedAPI = async (
    parameterStore: ParameterStore,
    formType?: 'single-quote' | 'combination-quote',
) => {
    let quotes = parameterStore.quote;
    if (formType === 'combination-quote' && parameterStore.quoteCombination) {
        quotes = parameterStore.quoteCombination;
    }

    // fetch info from form
    const nargs: NargsHashMapType = {};

    const productGuaranteed = quotes.productGuaranteed.value;
    const age = parameterStore.approval.age.value;

    const gender = parameterStore.approval.gender.value;

    nargs.productGuaranteed = {value: productGuaranteed};
    nargs.age = {value: age};
    nargs.gender = {value: gender};

    Object.entries(quotes).forEach(([n, p]) => {
        // what is this doing?
        const guardFilter = !quoteGuards.hasOwnProperty(n) || quoteGuards[n](parameterStore, quotes);
        if (!guardFilter) return;
        const value = p.value;
        nargs[n] = {
            value: value,
        };
    });

    // get quotes data
    let data: {
        state_id: string;
        gender_id: string;
        insured_age: number;
        face_amount: number;
        modal_premium: number;
    }[] = [];
    let faceAmountField = '';
    let doIXNCall = false;
    // values comes from json config file so its not static
    switch (nargs.productGuaranteed.value) {
        case Utils.MakeVarName(ProductGuaranteedValues.moo):
            data = [];
            doIXNCall = true;
            break;
        case Utils.MakeVarName(ProductGuaranteedValues.transamerica):
            data =
                (await API.RequestToThisOrigin(
                    `./guaranteed-data/transamerica.json?v=${ParameterStore.version}`,
                    'GET',
                    undefined,
                )) || [];
            faceAmountField = PGFields.specifiedFaceAmountTransGerber;
            break;
        case Utils.MakeVarName(ProductGuaranteedValues.great_western):
            data =
                (await API.RequestToThisOrigin(
                    `./guaranteed-data/great-western.json?v=${ParameterStore.version}`,
                    'GET',
                    undefined,
                )) || [];
            faceAmountField = PGFields.specifiedFaceAmountGW;
            break;
        case Utils.MakeVarName(ProductGuaranteedValues.gerber):
            data =
                (await API.RequestToThisOrigin(
                    `./guaranteed-data/gerber.json?v=${ParameterStore.version}`,
                    'GET',
                    undefined,
                )) || [];
            faceAmountField = PGFields.specifiedFaceAmountTransGerber;
            break;
        default:
            return {
                error: 'could not find products with the provided information.',
            };
    }

    if (doIXNCall) {
        const state = parameterStore.approval.state.value;
        const specifiedFaceAmount = quotes.specifiedFaceAmount.value;
        const premiumFrequency = quotes.premiumFrequency.value;
        const smokerRatingMOO = quotes.smokerRatingMOO.value;
        const termPeriod = quotes.termPeriod.value;
        const healthRatingMOO = quotes.healthRatingMOO.value;

        const bday = parameterStore.approval.bday.value;
        const bmonth = parameterStore.approval.bmonth.value;
        const byear = parameterStore.approval.byear.value;
        const nearestAge = AgeComputed(bday, bmonth, byear);

        nargs.premiumFrequency = {value: premiumFrequency};
        nargs.state = {value: state};
        nargs.specifiedFaceAmount = {value: specifiedFaceAmount};
        nargs.smokerRatingMOO = {value: smokerRatingMOO};
        nargs.termPeriod = {value: termPeriod};
        nargs.healthRatingMOO = {value: healthRatingMOO};
        nargs.nearestAge = {value: nearestAge};

        return await GuaranteedIXNCall(nargs);
    }

    // /get quotes data

    // find matching quote
    let quote: {
        state_id: string;
        gender_id: string;
        insured_age: number;
        face_amount: number;
        modal_premium: number;
    };
    data.forEach((el) => {
        if (
            el.insured_age === nargs.age.value &&
            el.face_amount === nargs[faceAmountField].value &&
            el.gender_id === nargs.gender.value
        ) {
            quote = el;
        }
    });
    // @ts-ignore
    quote = quote;
    // /find matching quote

    if (!quote) {
        return {
            error: 'could not find products with the provided information.',
        };
    }

    return {
        faceAmount: quote.face_amount,
        monthlyPremium: quote.modal_premium,
        productName: nargs.productGuaranteed.value,
        out: [
            {
                name: 'Carrier Product',
                value: nargs.productGuaranteed.value,
                type: 'string',
                fieldName: TemplateDataFields.productName,
            },
            {
                name: 'Face amount',
                value: quote.face_amount,
                type: 'currency',
                fieldName: TemplateDataFields.faceAmount,
            },
            {
                name: 'Monthly premium',
                value: quote.modal_premium,
                type: 'currency',
                fieldName: TemplateDataFields.monthlyPremium,
            },
        ],
        requestDetails: quote,
    };
};
